import React, { useEffect, useState, useCallback } from "react";
import {
  FileText, Download, Mail, AlertTriangle, TrendingDown, Leaf, Gauge, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { api, formatApiError } from "../../lib/api";
import { fmtKwh, fmtDateGB } from "../../lib/format";
import FinancialKpis from "../../components/FinancialKpis";
import DateInput from "../../components/DateInput";

const fmtGbp = (v) =>
  v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: Math.abs(v) >= 100 ? 0 : 2 })}`;
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

const STATUS_META = {
  on_target: { label: "On target", tone: "ok" },
  slightly_under: { label: "Slightly underperforming", tone: "warn" },
  underperforming: { label: "Underperforming — investigate", tone: "danger" },
  no_data: { label: "No production data for period", tone: "muted" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (ym) => {
  try {
    const [y, m] = ym.split("-");
    return `${MONTHS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
  } catch {
    return ym;
  }
};

export default function PerformanceReport({ projectId }) {
  const [start, setStart] = useState(
    () => new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10),
  );
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/monitoring/${projectId}/performance-report?start_date=${start}&end_date=${end}`,
      );
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [projectId, start, end]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(
        `/monitoring/${projectId}/performance-report.pdf?start_date=${start}&end_date=${end}`,
        { responseType: "blob" },
      );
      const filename = (res.headers["content-disposition"] || "")
        .match(/filename="?([^"]+)"?/)?.[1] || "performance-report.pdf";
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      setTimeout(() => { URL.revokeObjectURL(dl.href); dl.remove(); }, 200);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF export failed");
    } finally {
      setDownloading(false);
    }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(
        `/monitoring/${projectId}/performance-report/email?start_date=${start}&end_date=${end}`,
      );
      toast.success(`Emailed to ${(data.sent_to || []).join(", ") || "recipients"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  const k = report?.kpis || {};
  const sm = STATUS_META[report?.status] || STATUS_META.no_data;
  const chartData = (report?.monthly || []).map((m) => ({
    label: monthLabel(m.month),
    Actual: m.actual_kwh,
    Expected: m.expected_kwh,
  }));

  return (
    <div className="space-y-6" data-testid="performance-report-tab">
      {/* Controls */}
      <section className="swiss-card-flat p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label">Performance report</div>
            <div className="text-xs muted mt-1">
              Actual vs weather-adjusted expected yield · financial &amp; carbon impact
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 border border-[var(--line)] bg-white px-2 py-1"
                 data-testid="perf-range-pickers">
              <DateInput value={start} max={end}
                     onChange={(e) => setStart(e.target.value)}
                     className="text-[11px] font-mono border-0 focus:outline-none"
                     data-testid="perf-start-date" />
              <span className="muted text-[10px]">→</span>
              <DateInput value={end} min={start}
                     max={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setEnd(e.target.value)}
                     className="text-[11px] font-mono border-0 focus:outline-none"
                     data-testid="perf-end-date" />
            </div>
            <button onClick={load} disabled={loading} className="btn-ghost text-[10px]"
                    data-testid="perf-refresh-btn">
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={downloadPdf} disabled={downloading || !report}
                    className="btn-primary text-[10px] justify-center"
                    data-testid="perf-download-pdf-btn">
              {downloading ? <RefreshCw size={11} className="animate-spin" /> : <FileText size={11} />} PDF
            </button>
            <button onClick={emailReport} disabled={emailing || !report}
                    className="btn-ghost text-[10px]"
                    title="Email the PDF to the project's configured recipients"
                    data-testid="perf-email-btn">
              {emailing ? <RefreshCw size={11} className="animate-spin" /> : <Mail size={11} />} Email
            </button>
          </div>
        </div>
      </section>

      {!report ? (
        <div className="swiss-card-flat p-10 muted text-sm" data-testid="perf-empty">
          {loading ? "Loading performance report…" : "No report data."}
        </div>
      ) : (
        <>
          {/* Status banner */}
          <div className={`p-4 border-l-4 flex items-baseline gap-4 flex-wrap ${
            sm.tone === "ok" ? "bg-emerald-50 border-emerald-500"
              : sm.tone === "warn" ? "bg-amber-50 border-amber-500"
              : sm.tone === "danger" ? "bg-red-50 border-red-500"
              : "bg-[var(--surface-2)] border-zinc-400"}`}
               data-testid="perf-status-banner">
            <span className="label">Site health</span>
            <span className="font-display text-xl font-bold" data-testid="perf-status-label">{sm.label}</span>
            <span className="ml-auto font-mono text-sm muted" data-testid="perf-status-perf">
              {fmtPct(k.performance_vs_expected_pct)} of expected yield
            </span>
          </div>

          {/* KPI band */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 bg-[var(--surface-2)]"
               data-testid="perf-kpi-band">
            <Kpi label="Actual yield" value={fmtKwh(k.actual_kwh)} testid="perf-kpi-actual" />
            <Kpi label="Expected (weather-adj)" value={fmtKwh(k.expected_kwh)} testid="perf-kpi-expected" />
            <Kpi label="Performance Ratio" value={fmtPct(k.performance_ratio_pct)}
                 icon={<Gauge size={14} />} testid="perf-kpi-pr" />
            <Kpi label="Variance" value={fmtPct(k.variance_pct)}
                 tone={(k.variance_pct || 0) < 0 ? "danger" : "ok"} testid="perf-kpi-variance" />
          </div>

          {/* Impact cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="perf-impact-cards">
            <ImpactCard tone="danger" icon={<TrendingDown size={16} />}
                        label="Revenue lost to underperformance"
                        value={fmtGbp(k.revenue_lost_gbp)}
                        sub={`${fmtKwh(k.shortfall_kwh)} shortfall @ ${k.value_rate_p_per_kwh}p/kWh · ${k.value_rate_basis}`}
                        testid="perf-impact-revenue" />
            <ImpactCard tone="ok" icon={<Leaf size={16} />}
                        label="Carbon avoided"
                        value={`${((k.co2_avoided_kg || 0) / 1000).toFixed(2)} t CO₂e`}
                        sub={`${report.carbon_factor} kg/kWh grid factor · ${fmtKwh(k.actual_kwh)} generated`}
                        testid="perf-impact-carbon" />
            <ImpactCard tone="warn" icon={<AlertTriangle size={16} />}
                        label="Carbon not displaced"
                        value={`${(k.co2_lost_kg || 0).toLocaleString()} kg CO₂e`}
                        sub="from the energy shortfall"
                        testid="perf-impact-carbon-lost" />
          </div>

          {/* Financial yield & IRR */}
          {report.financial && <FinancialKpis data={report.financial} />}

          {/* Monthly chart */}
          <section className="swiss-card-flat p-6">
            <div className="label mb-3">Actual vs expected — monthly</div>
            {chartData.length === 0 ? (
              <div className="muted text-sm">No monthly data in this period.</div>
            ) : (
              <div style={{ width: "100%", height: 300 }} data-testid="perf-monthly-chart">
                <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={1}>
                  <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                    <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                           tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}MWh` : `${v}`)} />
                    <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12 }}
                             formatter={(v) => `${Number(v).toLocaleString()} kWh`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Expected" fill="#CBD5E1" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Actual" fill="var(--accent)" radius={[2, 2, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          {/* Anomalies */}
          <section className="swiss-card-flat p-6" data-testid="perf-anomalies">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="label">Underperformance anomalies</div>
              <div className="text-xs muted font-mono">
                {report.anomaly_count} flagged · threshold {report.threshold_pct}%
              </div>
            </div>
            {(report.anomalies || []).length === 0 ? (
              <div className="text-sm text-emerald-700" data-testid="perf-no-anomalies">
                No underperformance anomalies detected in this period. ✓
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="perf-anomaly-table">
                  <thead>
                    <tr className="text-left label border-b border-[var(--line)]">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3 text-right">Expected</th>
                      <th className="py-2 pr-3 text-right">Actual</th>
                      <th className="py-2 pr-3 text-right">Shortfall</th>
                      <th className="py-2 pr-3 text-right">Drop</th>
                      <th className="py-2">Flag</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs">
                    {report.anomalies.map((a, i) => (
                      <tr key={i} className="border-b border-[var(--line)]"
                          data-testid={`perf-anomaly-row-${i}`}>
                        <td className="py-1.5 pr-3">{fmtDateGB(a.date)}</td>
                        <td className="py-1.5 pr-3 text-right">{fmtKwh(a.expected_kwh)}</td>
                        <td className="py-1.5 pr-3 text-right">{fmtKwh(a.actual_kwh)}</td>
                        <td className="py-1.5 pr-3 text-right text-red-600">-{fmtKwh(a.shortfall_kwh)}</td>
                        <td className="py-1.5 pr-3 text-right text-red-600">-{fmtPct(a.drop_pct)}</td>
                        <td className="py-1.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                            a.kind === "zero_production" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                            {a.kind === "zero_production" ? "Zero output" : "Under target"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "muted", icon, testid }) {
  const tones = {
    ok: "text-emerald-700", danger: "text-red-600", muted: "text-[var(--ink)]",
  };
  return (
    <div className="bg-white p-5 border border-[var(--line)]" data-testid={testid}>
      <div className="label flex items-center gap-1.5">{icon}{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 font-mono ${tones[tone] || tones.muted}`}>{value}</div>
    </div>
  );
}

function ImpactCard({ tone, icon, label, value, sub, testid }) {
  const tones = {
    danger: "bg-red-50 border-red-200 text-red-700",
    ok: "bg-emerald-50 border-emerald-200 text-emerald-700",
    warn: "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <div className={`p-5 border ${tones[tone]}`} data-testid={testid}>
      <div className="label flex items-center gap-1.5 text-current opacity-80">{icon}{label}</div>
      <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
      <div className="text-xs mt-1 opacity-70 text-[var(--ink)]">{sub}</div>
    </div>
  );
}
